import { getFramework } from '@/framework';
import { Layer, type LayerInsertPosition } from '@/layers/layer';
import {
    type BrushData,
    type ILayerProps,
    type IVectorBorderRadiusProps,
    type IVectorEffectReflection,
    type IVectorEffectShadow,
    type ListStyleStruct,
    type TextAlignType,
    type TextContentPadding,
    type TextVerticalAlign,
    type TextWritingType,
} from '@/version-types';
import { type VectorSVGMaterial } from '../types';
import { Blur, type IBlur } from './blur';
import { BorderRadius, type IBorderRadius } from './border-radius';
import { FillColor, type IFillColor } from './fill-color';
import { FontBase, type IFontBase } from './font-base';
import { ListText, type IListText } from './list-text';
import { PieceColor, type IPieceColor } from './piece-color';
import { ReflectionEffect, type IReflectionEffect } from './reflection-effect';
import { ShadowEffect, type IShadowEffect } from './shadow-effect';
import { Shortcut, type IShortcut } from './shortcut';
import { Stroke, type IStroke } from './stroke';
import { TextAlignment, type ITextAlignment } from './text-alignment';
import { TextColor, type ITextColor } from './text-color';
import { TextDecoration, type ITextDecoration } from './text-decoration';
import { TextPadding, type ITextPadding } from './text-padding';
import { TextSpacing, type ITextSpacing } from './text-spacing';
import { WritingMode, type IWritingMode } from './writing-mode';

interface IVectorLayer
    extends
        IShortcut,
        IFillColor,
        IBlur,
        IBorderRadius,
        IStroke,
        IShadowEffect,
        IReflectionEffect,
        IPieceColor,
        IFontBase,
        ITextColor,
        ITextDecoration,
        IWritingMode,
        IListText,
        ITextAlignment,
        ITextSpacing,
        ITextPadding {}

export class VectorLayer extends Layer implements IVectorLayer {
    private shortcut: IShortcut;
    private fillColor: IFillColor;
    private blur: IBlur;
    private borderRadius: IBorderRadius;
    private stroke: IStroke;
    private shadowEffect: IShadowEffect;
    private reflectionEffect: IReflectionEffect;
    private pieceColor: IPieceColor;

    private fontBase: IFontBase;
    private textColor: ITextColor;
    private textDecoration: ITextDecoration;
    private writingMode: IWritingMode;
    private listText: IListText;
    private textAlignment: ITextAlignment;
    private textSpacing: ITextSpacing;
    private textPadding: ITextPadding;

    constructor(identity: string) {
        super(identity);

        const framework = getFramework(identity);

        this.shortcut = new Shortcut(framework, this);
        this.fillColor = new FillColor(framework);
        this.blur = new Blur(framework);
        this.borderRadius = new BorderRadius(framework);
        this.stroke = new Stroke(framework);
        this.shadowEffect = new ShadowEffect(framework);
        this.reflectionEffect = new ReflectionEffect(framework);
        this.pieceColor = new PieceColor(framework);

        this.fontBase = new FontBase(framework);
        this.textColor = new TextColor(framework);
        this.textDecoration = new TextDecoration(framework);
        this.writingMode = new WritingMode(framework);
        this.listText = new ListText(framework);
        this.textAlignment = new TextAlignment(framework);
        this.textSpacing = new TextSpacing(framework);
        this.textPadding = new TextPadding(framework);
    }

    addByBrushData(data: BrushData, options = { byMousePosition: false }) {
        return this.shortcut.addByBrushData(data, options);
    }

    addByVectorMaterial(
        material: VectorSVGMaterial,
        options: {
            byMousePosition?: boolean;
            keepOriginSize?: boolean;
            trackFunc?: string;
            position?: LayerInsertPosition;
        } = { byMousePosition: false, trackFunc: '' },
    ) {
        return this.shortcut.addByVectorMaterial(material, options);
    }

    replaceImageLayerToVectorLayer(imageLayer: ILayerProps, material: VectorSVGMaterial) {
        return this.shortcut.replaceImageLayerToVectorLayer(imageLayer, material);
    }

    setFillColor(layerID: string, color: string, options = { writeHistory: true }) {
        return this.fillColor.setFillColor(layerID, color, options);
    }

    setBlur(layerID: string, value: number, options = { writeHistory: true }) {
        return this.blur.setBlur(layerID, value, options);
    }

    setBorderRadius(
        layerID: string,
        value: number,
        options?: { corner?: keyof IVectorBorderRadiusProps; writeHistory?: boolean },
    ) {
        return this.borderRadius.setBorderRadius(layerID, value, options);
    }

    addStroke(layerID: string, config: { color: string; width?: number }) {
        return this.stroke.addStroke(layerID, config);
    }

    setStrokeVisibility(layerID: string, flag: boolean) {
        return this.stroke.setStrokeVisibility(layerID, flag);
    }

    removeStroke(layerID: string) {
        return this.stroke.removeStroke(layerID);
    }

    setStrokeStyle(layerID: string, config: { dasharray: string; linecap: string }) {
        return this.stroke.setStrokeStyle(layerID, config);
    }

    setStrokeWidth(layerID: string, width: number, options?: { writeHistory: boolean }) {
        return this.stroke.setStrokeWidth(layerID, width, options);
    }

    setStrokeColor(layerID: string, color: string, options?: { writeHistory: boolean }) {
        return this.stroke.setStrokeColor(layerID, color, options);
    }

    addShadow(layerID: string, shadow: IVectorEffectShadow) {
        return this.shadowEffect.addShadow(layerID, shadow);
    }

    removeShadow(layerID: string) {
        return this.shadowEffect.removeShadow(layerID);
    }

    setShadowVisibility(layerID: string, flag: boolean) {
        return this.shadowEffect.setShadowVisibility(layerID, flag);
    }

    adjustShadowProps(layerID: string, props: Partial<IVectorEffectShadow>, options = { writeHistory: true }) {
        return this.shadowEffect.adjustShadowProps(layerID, props, options);
    }

    addReflection(layerID: string, reflection: IVectorEffectReflection) {
        return this.reflectionEffect.addReflection(layerID, reflection);
    }

    removeReflection(layerID: string) {
        return this.reflectionEffect.removeReflection(layerID);
    }

    setReflectionVisibility(layerID: string, flag: boolean) {
        return this.reflectionEffect.setReflectionVisibility(layerID, flag);
    }

    adjustReflectionProps(layerID: string, props: Partial<IVectorEffectReflection>, options = { writeHistory: true }) {
        return this.reflectionEffect.adjustReflectionProps(layerID, props, options);
    }

    setPieceColor(layerID: string, index: number, color: string, options = { writeHistory: true }) {
        return this.pieceColor.setPieceColor(layerID, index, color, options);
    }

    // #region 文本相关
    async setFontFamily(layerID: string, fontFamily: string, materialID: string) {
        return this.fontBase.setFontFamily(layerID, fontFamily, materialID);
    }

    setFontSize(layerID: string, fontSize: number, options = { writeHistory: true }) {
        return this.fontBase.setFontSize(layerID, fontSize, options);
    }

    setItalicStyle(layerID: string, flag: boolean) {
        return this.fontBase.setItalicStyle(layerID, flag);
    }

    setBoldStyle(layerID: string, flag: boolean) {
        return this.fontBase.setBoldStyle(layerID, flag);
    }

    setTextColor(layerID: string, color: string, options = { writeHistory: true }) {
        return this.textColor.setTextColor(layerID, color, options);
    }

    setTextOpacity(layerID: string, opacity: number, options = { writeHistory: true }) {
        return this.textColor.setTextOpacity(layerID, opacity, options);
    }

    setUnderline(layerID: string, flag: boolean) {
        return this.textDecoration.setUnderline(layerID, flag);
    }

    setLineThrough(layerID: string, flag: boolean) {
        return this.textDecoration.setLineThrough(layerID, flag);
    }

    setWritingMode(layerID: string, writingMode: TextWritingType) {
        return this.writingMode.setWritingMode(layerID, writingMode);
    }

    applyListText(layerID: string, kind: ListStyleStruct) {
        return this.listText.applyListText(layerID, kind);
    }

    cancelListText(layerID: string) {
        return this.listText.cancelListText(layerID);
    }

    setTextAlign(layerID: string, value: TextAlignType) {
        return this.textAlignment.setTextAlign(layerID, value);
    }

    setVerticalAlign(layerID: string, value: TextVerticalAlign) {
        return this.textAlignment.setVerticalAlign(layerID, value);
    }

    setLineSpacing(layerID: string, value: number, options = { writeHistory: false }) {
        return this.textSpacing.setLineSpacing(layerID, value, options);
    }

    setLetterSpacing(layerID: string, value: number, options = { writeHistory: false }) {
        return this.textSpacing.setLetterSpacing(layerID, value, options);
    }

    setTextPadding(layerID: string, padding: TextContentPadding, options = { writeHistory: false }) {
        return this.textPadding.setTextPadding(layerID, padding, options);
    }
}
